import axios from 'axios';
import { sendWebViewMeInfoMessage } from '../utils/getMyInfo';

// Axios 인스턴스 생성
const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    timeout: Number(process.env.REACT_APP_NETWORK_TIME_OUT_SEC) * 1000,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    },
});

// 요청 인터셉터 - 인증 토큰 자동 추가
apiClient.interceptors.request.use(
    config => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        if (process.env.REACT_APP_IS_SHOW_NETWORK_LOG === 'true')
            console.log(
                `request ${config.method?.toUpperCase()} ${config.url}`,
                config.data ? `data: ${JSON.stringify(config.data)}` : '',
                config.params ? `params: ${JSON.stringify(config.params)}` : ''
            );

        return config;
    },
    error => {
        console.error('[API Request Error]', error);
        return Promise.reject(error);
    }
);

// 응답 인터셉터 - 에러 처리
apiClient.interceptors.response.use(
    response => {
        if (process.env.REACT_APP_IS_SHOW_NETWORK_LOG === 'true')
            console.log(
                `response ${response.config.method?.toUpperCase()} ${response.config.url} => ${JSON.stringify(response.data)}`
            );
        return response;
    },
    error => {
        // 전체 URL 구성
        const fullUrl = error.config?.baseURL
            ? `${error.config.baseURL}${error.config.url}`
            : error.config?.url || 'unknown';

        const errorInfo = {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            url: error.config?.url,
            fullUrl: fullUrl,
            baseURL: error.config?.baseURL || process.env.REACT_APP_API_BASE_URL,
            method: error.config?.method,
            data: error.response?.data,
            code: error.code, // 'ECONNABORTED', 'ERR_NETWORK', 'ERR_CANCELED' 등
            timeout: error.config?.timeout,
        };

        console.error('[API Error]', errorInfo);

        // Network Error인 경우 추가 정보 출력
        if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
            console.error('[Network Error Details]', {
                baseURL: error.config?.baseURL || process.env.REACT_APP_API_BASE_URL,
                fullUrl: fullUrl,
                timeout: error.config?.timeout,
                hasToken: !!localStorage.getItem('accessToken'),
                errorCode: error.code,
                errorMessage: error.message,
                possibleCauses: [
                    '서버가 응답하지 않음 (서버 다운 또는 네트워크 연결 문제)',
                    'CORS 정책 위반',
                    'baseURL이 잘못 설정됨',
                    '요청 타임아웃',
                    '인터넷 연결 문제'
                ]
            });
        }

        // 401 인증 실패 시 자동 로그아웃
        if (error.response?.status === 401) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            // window.location.href = '/login';
        }

        return Promise.reject(error);
    }
);

// ============================================================================
// 인증 API (OAuth 2.0)
// ============================================================================
export const authorizeAPI = {
    // 인증 토큰 발급 (password, sns, refresh_token grant types 지원)
    getToken: async tokenData => {
        const response = await apiClient.post('/oauth/token', {
            grant_type: tokenData.grant_type,
            client_id: 'f11f391ea770f9daeef880a66508d733',
            client_secret: '122015ebfb35e53469e8d2347524ff59',
            ...tokenData,
        });

        // 토큰 저장
        if (response.data.access_token) {
            localStorage.setItem('accessToken', response.data.access_token);
        }
        if (response.data.refresh_token) {
            localStorage.setItem('refreshToken', response.data.refresh_token);
        }

        return response.data;
    },

    // 인증 토큰 삭제
    revokeToken: async () => {
        try {
            await apiClient.get('/oauth/revoke');
        } catch (error) {
            console.warn('Token revocation failed:', error);
        }

        // 로컬 스토리지 정리
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
    },
};

// ============================================================================
// 앱 API
// ============================================================================
export const appAPI = {
    // 앱 조회
    getApps: async () => {
        const response = await apiClient.get('/apps');
        return response.data;
    },

    // 특정 앱 정보 조회
    getAppById: async appId => {
        const response = await apiClient.get(`/apps/${appId}`);
        return response.data;
    },

    // 특정 앱 이름으로 조회
    getAppByName: async name => {
        const response = await apiClient.get(`/apps/by-name/${name}`);
        return response.data;
    },

    // 특정 앱 client id로 조회
    getAppByClientId: async clientId => {
        const response = await apiClient.get(`/apps/by-clientid/${clientId}`);
        return response.data;
    },

    // 앱 수정 (관리자 전용)
    updateApp: async appData => {
        const response = await apiClient.post('/app', {
            id: appData.id,
            name: appData.name,
            state: appData.state,
        });
        return response.data;
    },

    // 앱 생성 (관리자 전용)
    registerApp: async appData => {
        const response = await apiClient.post('/app/register', {
            name: appData.name,
        });
        return response.data;
    },
};

// ============================================================================
// 챌린지 API
// ============================================================================
export const challengeAPI = {
    // 챌린지 조회
    getChallenges: async (params = {}) => {
        const response = await apiClient.get('/challenges', { params });
        return response.data;
    },

    // 챌린지 진행 상태 조회
    getChallengeProgress: async (params = {}) => {
        const response = await apiClient.get('/challenges-progress', { params });
        return response.data;
    },

    // 챌린지 수정
    updateChallenge: async challengeData => {
        const response = await apiClient.post('/challenge', {
            id: challengeData.id,
            name: challengeData.name,
            contentId: challengeData.contentId,
            type: challengeData.type,
            state: challengeData.state,
            startDate: challengeData.startDate,
            endDate: challengeData.endDate,
            period: challengeData.period,
            targetUnitCount: challengeData.targetUnitCount,
            targetMaxCount: challengeData.targetMaxCount,
            targetMinutes: challengeData.targetMinutes,
            targetMaxMinutes: challengeData.targetMaxMinutes,
            targetDailyCount: challengeData.targetDailyCount,
            rewardUnitPoint: challengeData.rewardUnitPoint,
            rewardMaxPoint: challengeData.rewardMaxPoint,
            info: challengeData.info,
        });
        return response.data;
    },

    // 챌린지 등록
    registerChallenge: async challengeData => {
        const response = await apiClient.post('/challenge/register', {
            name: challengeData.name,
            contentId: challengeData.contentId,
            type: challengeData.type,
            startDate: challengeData.startDate,
            endDate: challengeData.endDate,
            period: challengeData.period,
            targetUnitCount: challengeData.targetUnitCount,
            targetMaxCount: challengeData.targetMaxCount,
            targetMinutes: challengeData.targetMinutes,
            targetMaxMinutes: challengeData.targetMaxMinutes,
            targetDailyCount: challengeData.targetDailyCount,
            rewardUnitPoint: challengeData.rewardUnitPoint,
            rewardMaxPoint: challengeData.rewardMaxPoint,
            info: challengeData.info,
        });
        return response.data;
    },
};

// ============================================================================
// 챔피언 API
// ============================================================================
export const championAPI = {
    // 챔피언 조회
    getChampions: async (params = {}) => {
        const response = await apiClient.get('/champions', { params });
        return response.data;
    },

    // 챔피언 수정
    updateChampion: async championData => {
        const response = await apiClient.post('/champion', championData);
        return response.data;
    },

    // 챔피언 등록
    registerChampion: async championData => {
        // formData 전체를 전달하되, null이나 undefined가 아닌 값만 포함
        // const requestData = {};
        // Object.keys(championData).forEach(key => {
        //     if (championData[key] !== null && championData[key] !== undefined) {
        //         // contentIndex를 contentId로 매핑 (API가 contentId를 받는 경우)
        //         if (key === 'contentIndex') {
        //             requestData.contentIndex = championData[key];
        //         } else if (key !== 'contentName') {
        //             // contentName은 서버에 전달하지 않음 (표시용)
        //             requestData[key] = championData[key];
        //         }
        //     }
        // });

        const response = await apiClient.post('/champion/register', championData);
        return response.data;
    },
};

// ============================================================================
// 콘텐츠 API
// ============================================================================
export const contentAPI = {
    // 콘텐츠 조회
    getContents: async (params = {}) => {
        const response = await apiClient.get('/contents', { params });
        return response.data;
    },

    // 콘텐츠 수정
    updateContent: async contentData => {
        // 항상 FormData로 전송
        const formData = new FormData();
        if (contentData.index) formData.append('index', contentData.index);
        if (contentData.ko_name) formData.append('koName', contentData.ko_name);
        if (contentData.ja_name) formData.append('jaName', contentData.ja_name);
        if (contentData.de_name) formData.append('deName', contentData.de_name);
        if (contentData.en_name) formData.append('enName', contentData.en_name);
        if (contentData.type !== undefined) formData.append('type', contentData.type);
        if (contentData.state !== undefined) formData.append('state', contentData.state);
        if (contentData.endurance !== undefined) formData.append('endurance', contentData.endurance);
        if (contentData.strength !== undefined) formData.append('strength', contentData.strength);
        if (contentData.balance !== undefined) formData.append('balance', contentData.balance);
        if (contentData.flexibility !== undefined) formData.append('flexibility', contentData.flexibility);
        if (contentData.reaction !== undefined) formData.append('reaction', contentData.reaction);
        if (contentData.cooperation !== undefined) formData.append('cooperation', contentData.cooperation);
        if (contentData.price !== undefined) formData.append('price', contentData.price);
        if (contentData.info) formData.append('info', contentData.info);
        if (contentData.file instanceof File) {
            formData.append('file', contentData.file); // 컨텐츠 아이콘 이미지 파일 경로(keep in last order.)
        }

        const response = await apiClient.post('/content', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    // 콘텐츠 등록
    registerContent: async contentData => {
        // 항상 FormData로 전송
        const formData = new FormData();
        if (contentData.index) formData.append('index', contentData.index);
        if (contentData.ko_name) formData.append('koName', contentData.ko_name);
        if (contentData.ja_name) formData.append('jaName', contentData.ja_name);
        if (contentData.de_name) formData.append('deName', contentData.de_name);
        if (contentData.en_name) formData.append('enName', contentData.en_name);
        if (contentData.type !== undefined) formData.append('type', contentData.type);
        if (contentData.endurance !== undefined) formData.append('endurance', contentData.endurance);
        if (contentData.strength !== undefined) formData.append('strength', contentData.strength);
        if (contentData.balance !== undefined) formData.append('balance', contentData.balance);
        if (contentData.flexibility !== undefined) formData.append('flexibility', contentData.flexibility);
        if (contentData.reaction !== undefined) formData.append('reaction', contentData.reaction);
        if (contentData.cooperation !== undefined) formData.append('cooperation', contentData.cooperation);
        if (contentData.price !== undefined) formData.append('price', contentData.price);
        if (contentData.info) formData.append('info', contentData.info);
        if (contentData.file instanceof File) {
            formData.append('file', contentData.file); // 컨텐츠 아이콘 이미지 파일 경로(keep in last order.)
        }

        const response = await apiClient.post('/content/register', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },
};

// ============================================================================
// 대시보드 API
// ============================================================================
export const dashboardAPI = {
    // supervisor 대시보드 정보 조회 (supervisor 전용. 현재 지원 안함)
    getSupervisorDashboard: async (params = {}) => {
        const response = await apiClient.get('/supervisor-dashboard', {
            params,
        });
        return response.data;
    },

    // admin 대시보드 정보 조회 (관리자 전용. 현재 지원 안함)
    getAdminDashboard: async (params = {}) => {
        const response = await apiClient.get('/admin-dashboard', { params });
        return response.data;
    },
};

// ============================================================================
// 디딤 인증 API
// ============================================================================
export const didimAuthAPI = {
    // 디딤 인증 등록
    //{
    // oneOf ->	
    // {
    // didimId*	[...]
    // userId*	[...]
    // expireDate	[...]
    // }
    // {
    // didimUid*	[...]
    // userId*	[...]
    // expireDate	[...]
    // }
    // }
    registerDidimAuth: async didimAuthData => {
        const response = await apiClient.post('/didim-auth/register', {
            ...didimAuthData
        });
        return response.data;
    },
};

// ============================================================================
// 디딤 API (머신)
// ============================================================================
export const didimAPI = {
    // 디딤 조회
    getDidims: async (params = {}) => {
        const response = await apiClient.get('/didims', { params });
        return response.data;
    },

    // 디딤 수정
    updateDidim: async didimData => {
        const response = await apiClient.post('/didim', {
            id: didimData.id,
            organizationId: didimData.organizationId,
            name: didimData.name,
            uid: didimData.uid,
            version: didimData.version,
            state: didimData.state,
            status: didimData.status,
            info: didimData.info,
        });
        return response.data;
    },

    // 디딤 등록
    registerDidim: async didimData => {
        const response = await apiClient.post('/didim/register', {
            organizationId: didimData.organizationId,
            name: didimData.name,
            uid: didimData.uid,
            version: didimData.version,
            state: didimData.state,
            status: didimData.status,
            info: didimData.info,
        });
        return response.data;
    },

    // 디딤 등록 해제
    unregisterDidim: async didimData => {
        const response = await apiClient.post('/didim/unregister', {
            id: didimData.id,
        });
        return response.data;
    },

    // 디딤 상태 업데이트
    updateDidimStatus: async statusData => {
        const response = await apiClient.post('/didim-status', {
            name: statusData.name,
            uid: statusData.uid,
            version: statusData.version,
            status: statusData.status,
            playingContentId: statusData.playingContentId,
            playingChampionId: statusData.playingChampionId,
            players: statusData.players,
        });
        return response.data;
    },
};

// ============================================================================
// 이벤트 API
// ============================================================================
export const eventAPI = {
    // 이벤트 조회
    getEvents: async (params = {}) => {
        const response = await apiClient.get('/events', { params });
        return response.data;
    },

    // 이벤트 참여 조회
    getEventParticipations: async (params = {}) => {
        const response = await apiClient.get('/event-participations', {
            params,
        });
        return response.data;
    },

    // 이벤트 참여 집계 조회
    getEventParticipationsStatistics: async (params = {}) => {
        const response = await apiClient.get('/event-participations-statistics', { params });
        return response.data;
    },

    // 이벤트 수정 (관리자 전용)
    updateEvent: async eventData => {
        const formData = new FormData();
        Object.keys(eventData).forEach(key => {
            if (eventData[key] !== null && eventData[key] !== undefined) {
                formData.append(key, eventData[key]);
            }
        });

        const response = await apiClient.post('/event', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    // 이벤트 등록 (관리자 전용)
    registerEvent: async eventData => {
        const formData = new FormData();
        Object.keys(eventData).forEach(key => {
            if (eventData[key] !== null && eventData[key] !== undefined) {
                formData.append(key, eventData[key]);
            }
        });

        const response = await apiClient.post('/event/register', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    //이벤트 참여
    participateEvent: async eventData => {
        const response = await apiClient.post('/event/participate', {
            userId: eventData.userId,
            eventId: eventData.eventId,
            usedPoint: eventData.usedPoint,
        });
        return response.data;
    }
};

// ============================================================================
// 선물 API (포인트선물)
// ============================================================================
export const giftAPI = {
    // 선물 조회
    getGifts: async (params = {}) => {
        const response = await apiClient.get('/gifts', { params });
        return response.data;
    },

    // 선물 수정 (관리자 전용)
    updateGift: async giftData => {
        const response = await apiClient.post('/gift', {
            id: giftData.id,
            userId: giftData.userId,
            organizationId: giftData.organizationId,
            senderUserId: giftData.senderUserId,
            state: giftData.state,
            point: giftData.point,
            message: giftData.message,
        });
        return response.data;
    },

    // 선물 등록
    registerGift: async giftData => {
        const response = await apiClient.post('/gift/register', {
            userId: giftData.userId,
            organizationId: giftData.organizationId,
            senderUserId: giftData.senderUserId,
            point: giftData.point,
            message: giftData.message,
        });
        return response.data;
    },
};

// ============================================================================
// 로그 API (플레이 로그, 기간별 플레이 정보, 포인트 로그, 기관 포인트 로그, 기간별 기관 포인트 정보, 기관 포인트 기여도 로그)
// ============================================================================
export const logAPI = {
    // 플레이 로그 조회
    getPlayLogs: async (params = {}) => {
        const response = await apiClient.get('/play-logs', { params });
        return response.data;
    },

    // 기간별 플레이 정보 조회
    getInfosByPeriod: async (params = {}) => {
        const response = await apiClient.get('/play-infos-by-period', { params });
        return response.data;
    },

    // 포인트 로그 조회
    getPointLogs: async (params = {}) => {
        const response = await apiClient.get('/point-logs', { params });
        return response.data;
    },

    // 기관 포인트 로그 조회 (관리자 전용)
    getOrganizationPointLogs: async (params = {}) => {
        const response = await apiClient.get('/organization-point-logs', {
            params,
        });
        return response.data;
    },

    // 기간별 기관 포인트 정보 조회 (관리자 전용)
    getOrganizationPointInfosByPeriod: async (params = {}) => {
        const response = await apiClient.get('/organization-point-infos-by-period', { params });
        return response.data;
    },

    // 기관 포인트 기여도 로그 조회 (관리자 전용)
    getOrganizationPointContributionLogs: async (params = {}) => {
        const response = await apiClient.get('/organization-point-contribution-logs', { params });
        return response.data;
    },
};

// ============================================================================
// 기관 멤버십 API
// ============================================================================
export const membershipAPI = {
    // 기관 멤버십 조회
    getMemberships: async (params = {}) => {
        const response = await apiClient.get('/memberships', { params });
        return response.data;
    },

    // 기관 멤버십 수정
    updateMembership: async membershipData => {
        const response = await apiClient.post('/membership', {
            userId: membershipData.userId,
            organizationId: membershipData.organizationId,
            division: membershipData.division,
            section: membershipData.section,
            type: membershipData.type,
            state: membershipData.state,
            info: membershipData.info,
        });
        return response.data;
    },

    // 기관 멤버십 등록
    registerMembership: async membershipData => {
        const response = await apiClient.post('/membership/register', {
            userId: membershipData.userId,
            organizationId: membershipData.organizationId,
            division: membershipData.division,
            section: membershipData.section,
            type: membershipData.type || 0, // 기본값 0 (매니저)
            state: membershipData.state || 0, // 기본값 0 (정상)
            info: membershipData.info,
        });
        return response.data;
    },


    // 기관 멤버십 삭제
    unregisterMembership: async membershipData => {
        const response = await apiClient.post('/membership/unregister', {
            userId: membershipData.userId,
            organizationId: membershipData.organizationId,
        });
        return response.data;
    },
};

// ============================================================================
// 사용자 API
// ============================================================================
export const userAPI = {
    // 나의 정보 조회
    getMyInfo: async () => {
        const response = await apiClient.get('/users/me');
        // 응답 성공 시 WebView 메시지 전송
        if (response?.data?.data?.user) {
            const userData = response.data.data.user;
            // localStorage 업데이트
            localStorage.setItem('meInfo', JSON.stringify(userData));
            // WebView 메시지 전송 (비동기로 실행하여 API 응답 지연 방지)
            setTimeout(() => {
                sendWebViewMeInfoMessage(userData);
            }, 0);
        }

        return response.data;
    },

    // 유저 조회 (관리자 전용)
    getUsers: async (userId, params = {}) => {
        const response = await apiClient.get(`/users/${userId}`, { params });
        return response.data;
    },

    // 유저 여부 확인 (로그인 아이디, 닉네임, 이메일 이용)
    checkUserExists: async checkData => {
        const response = await apiClient.post('/user/exists', {
            ...checkData,
            client_id: 'f11f391ea770f9daeef880a66508d733',
            client_secret: '122015ebfb35e53469e8d2347524ff59',
        });
        return response.data;
    },

    // 아이디 찾기
    findUserId: async findData => {
        const response = await apiClient.post('/user/find-id', {
            name: findData.name,
            birth: findData.birth,
            client_id: 'f11f391ea770f9daeef880a66508d733',
            client_secret: '122015ebfb35e53469e8d2347524ff59',
        });
        return response.data;
    },

    // 패스워드 찾기 (임시 패스워드 발급)
    findPassword: async findData => {
        const response = await apiClient.post('/user/find-password', {
            loginId: findData.loginId,
            client_id: 'f11f391ea770f9daeef880a66508d733',
            client_secret: '122015ebfb35e53469e8d2347524ff59',
        });
        return response.data;
    },

    // 회원가입 시 프로파일 이미지 설정
    initProfileImage: async profileData => {
        const formData = new FormData();
        formData.append('loginId', profileData.loginId);
        formData.append('name', profileData.name);
        formData.append('client_id', 'f11f391ea770f9daeef880a66508d733');
        formData.append('client_secret', '122015ebfb35e53469e8d2347524ff59');
        if (profileData.file) {
            formData.append('file', profileData.file);
        }

        const response = await apiClient.post(
            '/user/init-profile-image',
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );
        return response.data;
    },

    // 유저 수정
    updateUser: async userData => {
        const response = await apiClient.post('/user', {
            id: userData.id,
            grade: userData.grade,
            name: userData.name,
            nickname: userData.nickname,
            birth: userData.birth,
            gender: userData.gender,
            email: userData.email,
            mobile: userData.mobile,
            mainOrganizationId: userData.mainOrganizationId,
            info: userData.info,
        });
        return response.data;
    },

    // 유저 가입
    registerUser: async userData => {
        const response = await apiClient.post('/user/register', {
            loginId: userData.loginId,
            loginPwd: userData.loginPwd,
            snsType: userData.snsType,
            snsId: userData.snsId,
            grade: userData.grade,
            name: userData.name,
            nickname: userData.nickname,
            birth: userData.birth,
            gender: userData.gender,
            email: userData.email,
            mobile: userData.mobile,
            mainOrganizationId: userData.mainOrganizationId,
            info: userData.info,
            client_id: 'f11f391ea770f9daeef880a66508d733',
            client_secret: '122015ebfb35e53469e8d2347524ff59',
        });
        return response.data;
    },

    // 유저 탈퇴 (관리자 전용)
    unregisterUser: async userData => {
        const response = await apiClient.post('/user/unregister', {
            type: userData.type,
            userId: userData.userId,
        });
        return response.data;
    },

    // 유저 패스워드 인증
    certifyPassword: async certifyData => {
        const response = await apiClient.post('/user/certify-password', {
            userId: certifyData.userId,
            password: certifyData.password,
        });
        return response.data;
    },

    // 유저 패스워드 변경
    changePassword: async passwordData => {
        const response = await apiClient.post('/user/change-password', {
            loginId: passwordData.loginId,
            currentPassword: passwordData.currentPassword,
            newPassword: passwordData.newPassword,
            client_id: 'f11f391ea770f9daeef880a66508d733',
            client_secret: '122015ebfb35e53469e8d2347524ff59',
        });
        return response.data;
    },

    resetPassword: async passwordData => {
        const response = await apiClient.post('/user/reset-password', {
            loginId: passwordData.loginId,
            newPassword: passwordData.newPassword,
        });
        return response.data;
    },

    // 업로드 인증 이미지 (파일 보내지 않으면 삭제)
    uploadAuthImage: async uploadData => {
        const formData = new FormData();
        if (uploadData.userId) {
            formData.append('userId', uploadData.userId);
        }
        if (uploadData.file) {
            formData.append('file', uploadData.file);
        }

        const response = await apiClient.post(
            '/user/upload-auth-image',
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );
        return response.data;
    },

    // 업로드 프로파일 이미지 (파일 보내지 않으면 삭제)
    uploadProfileImage: async uploadData => {
        const formData = new FormData();
        if (uploadData.userId) {
            formData.append('userId', uploadData.userId);
        }
        if (uploadData.file) {
            formData.append('file', uploadData.file);
        }

        const response = await apiClient.post(
            '/user/upload-profile-image',
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );
        return response.data;
    },
};

// ============================================================================
// 공지 API
// ============================================================================
export const noticeAPI = {
    // 공지 조회
    getNotices: async (params = {}) => {
        const response = await apiClient.get('/notices', { params });
        return response.data;
    },

    // 공지 수정 (관리자 전용)
    updateNotice: async noticeData => {
        const formData = new FormData();
        Object.keys(noticeData).forEach(key => {
            if (noticeData[key] !== null && noticeData[key] !== undefined) {
                formData.append(key, noticeData[key]);
            }
        });

        const response = await apiClient.post('/notice', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    // 공지 등록 (관리자 전용)
    registerNotice: async noticeData => {
        const formData = new FormData();
        Object.keys(noticeData).forEach(key => {
            if (noticeData[key] !== null && noticeData[key] !== undefined) {
                formData.append(key, noticeData[key]);
            }
        });

        const response = await apiClient.post('/notice/register', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },
};

// ============================================================================
// FAQ API
// ============================================================================
export const faqAPI = {
    // FAQ 조회
    getFaqs: async (params = {}) => {
        const response = await apiClient.get('/faqs', { params });
        return response.data;
    },

    // FAQ 수정 (관리자 전용)
    updateFaq: async faqData => {
        const response = await apiClient.post('/faq', {
            id: faqData.id,
            category: faqData.category,
            state: faqData.state,
            question: faqData.question,
            answer: faqData.answer,
        });
        return response.data;
    },

    // FAQ 등록 (관리자 전용)
    registerFaq: async faqData => {
        const response = await apiClient.post('/faq/register', {
            category: faqData.category,
            state: faqData.state,
            question: faqData.question,
            answer: faqData.answer,
        });
        return response.data;
    },
};

// ============================================================================
// 알림 API
// ============================================================================
export const notificationAPI = {
    // 알림 조회
    getNotifications: async (params = {}) => {
        const response = await apiClient.get('/notifications', { params });
        return response.data;
    },

    // 알림 수정 (관리자 전용)
    updateNotification: async notificationData => {
        const response = await apiClient.post('/notification', {
            id: notificationData.id,
            organizationId: notificationData.organizationId,
            userId: notificationData.userId,
            type: notificationData.type,
            state: notificationData.state,
            startDate: notificationData.startDate,
            title: notificationData.title,
            content: notificationData.content,
        });
        return response.data;
    },

    // 알림 등록 (관리자 전용)
    registerNotification: async notificationData => {
        const response = await apiClient.post('/notification/register', {
            organizationId: notificationData.organizationId,
            userId: notificationData.userId,
            type: notificationData.type,
            state: notificationData.state,
            startDate: notificationData.startDate,
            title: notificationData.title,
            content: notificationData.content,
        });
        return response.data;
    },
};

// ============================================================================
// 사용자 세팅 API
// ============================================================================
export const settingAPI = {
    // 사용자 세팅 조회
    getSettings: async (params = {}) => {
        const response = await apiClient.get('/settings', { params });
        return response.data;
    },

    // 사용자 세팅 수정
    updateSetting: async settingData => {
        const response = await apiClient.post('/setting', {
            name: settingData.name,
            value: settingData.value,
            state: settingData.state,
        });
        return response.data;
    },

    // 사용자 세팅 등록
    registerSetting: async settingData => {
        const response = await apiClient.post('/setting/register', {
            name: settingData.name,
            value: settingData.value,
        });
        return response.data;
    },
};

// ============================================================================
// 스킬( 폐지구력, 근력, 평형성, 유연성, 순발력, 협응력 ...) API
// ============================================================================
export const skillAPI = {
    // 스킬 조회
    getSkills: async (params = {}) => {
        const response = await apiClient.get('/skills', { params });
        return response.data;
    },
};

// ============================================================================
// 시스템 컨피그 API
// ============================================================================
export const sysconfigAPI = {
    // 시스템 컨피그 조회
    getSysconfigs: async (params = {}) => {
        const response = await apiClient.get('/sysconfigs', { params });
        return response.data;
    },

    // 시스템 컨피그 수정
    updateSysconfig: async sysconfigData => {
        const response = await apiClient.post('/sysconfig', {
            organizationId: sysconfigData.organizationId,
            name: sysconfigData.name,
            value: sysconfigData.value,
            state: sysconfigData.state,
        });
        return response.data;
    },

    // 시스템 컨피그 등록
    registerSysconfig: async sysconfigData => {
        const response = await apiClient.post('/sysconfig/register', {
            organizationId: sysconfigData.organizationId,
            name: sysconfigData.name,
            value: sysconfigData.value,
        });
        return response.data;
    },
};

// ============================================================================
// 기관 API
// ============================================================================
export const organizationAPI = {
    // 기관 조회
    getOrganizations: async (params = {}) => {
        const response = await apiClient.get('/organizations', { params });
        return response.data;
    },

    // 기관 조회
    postOrganizations: async (params = {}) => {
        const response = await apiClient.post('/organizations', {
            ...params,
            client_id: 'f11f391ea770f9daeef880a66508d733',
            client_secret: '122015ebfb35e53469e8d2347524ff59',

        });
        return response.data;
    },

    // 기관 수정 (관리자 전용)
    updateOrganization: async organizationData => {
        // 항상 FormData로 전송
        const formData = new FormData();
        formData.append('id', organizationData.id);
        if (organizationData.userId !== undefined) formData.append('userId', organizationData.userId);
        if (organizationData.name) formData.append('name', organizationData.name);
        if (organizationData.type !== undefined) formData.append('type', organizationData.type);
        if (organizationData.state !== undefined) formData.append('state', organizationData.state);
        if (organizationData.address) formData.append('address', organizationData.address);
        if (organizationData.addressDetail) formData.append('addressDetail', organizationData.addressDetail);
        if (organizationData.zipcode) formData.append('zipcode', organizationData.zipcode);
        if (organizationData.phone) formData.append('phone', organizationData.phone);
        if (organizationData.email) formData.append('email', organizationData.email);
        if (organizationData.website) formData.append('website', organizationData.website);
        if (organizationData.country) formData.append('country', organizationData.country);
        if (organizationData.region) formData.append('region', organizationData.region);
        if (organizationData.latitude) formData.append('latitude', organizationData.latitude);
        if (organizationData.longitude) formData.append('longitude', organizationData.longitude);
        if (organizationData.divisionSections) formData.append('divisionSections', organizationData.divisionSections);
        if (organizationData.contactInfo) formData.append('contactInfo', organizationData.contactInfo);
        if (organizationData.stateInfo) formData.append('stateInfo', organizationData.stateInfo);
        if (organizationData.info) formData.append('info', organizationData.info);
        if (organizationData.file instanceof File) {
            formData.append('file', organizationData.file);
        }

        const response = await apiClient.post('/organization', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    // 기관 등록 (관리자 전용)
    registerOrganization: async organizationData => {
        // 항상 FormData로 전송
        const formData = new FormData();
        formData.append('userId', organizationData.userId);
        formData.append('name', organizationData.name);
        formData.append('type', organizationData.type);
        if (organizationData.state) formData.append('state', organizationData.state);
        if (organizationData.address) formData.append('address', organizationData.address);
        if (organizationData.addressDetail) formData.append('addressDetail', organizationData.addressDetail);
        if (organizationData.zipcode) formData.append('zipcode', organizationData.zipcode);
        if (organizationData.phone) formData.append('phone', organizationData.phone);
        if (organizationData.email) formData.append('email', organizationData.email);
        if (organizationData.website) formData.append('website', organizationData.website);
        if (organizationData.country) formData.append('country', organizationData.country);
        if (organizationData.region) formData.append('region', organizationData.region);
        if (organizationData.latitude) formData.append('latitude', organizationData.latitude);
        if (organizationData.longitude) formData.append('longitude', organizationData.longitude);
        if (organizationData.divisionSections) formData.append('divisionSections', organizationData.divisionSections);
        if (organizationData.contactInfo) formData.append('contactInfo', organizationData.contactInfo);
        if (organizationData.stateInfo) formData.append('stateInfo', organizationData.stateInfo);
        if (organizationData.info) formData.append('info', organizationData.info);
        if (organizationData.file instanceof File) {
            formData.append('file', organizationData.file);
        }

        const response = await apiClient.post('/organization/register', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    // 기관 삭제 (관리자 전용)
    unregisterOrganization: async organizationData => {
        const response = await apiClient.post('/organization/unregister', {
            id: organizationData.id,
        });
        return response.data;
    },

    // 기관 신청
    applyOrganization: async organizationApplyData => {
        // 항상 FormData로 전송
        const formData = new FormData();
        if (organizationApplyData.loginId) formData.append('loginId', organizationApplyData.loginId);
        if (organizationApplyData.loginPwd) formData.append('loginPwd', organizationApplyData.loginPwd);
        if (organizationApplyData.userName) formData.append('userName', organizationApplyData.userName);
        if (organizationApplyData.name) formData.append('name', organizationApplyData.name);
        if (organizationApplyData.type !== undefined) formData.append('type', organizationApplyData.type);
        if (organizationApplyData.address) formData.append('address', organizationApplyData.address);
        if (organizationApplyData.addressDetail) formData.append('addressDetail', organizationApplyData.addressDetail);
        if (organizationApplyData.zipcode) formData.append('zipcode', organizationApplyData.zipcode);
        if (organizationApplyData.phone) formData.append('phone', organizationApplyData.phone);
        if (organizationApplyData.email) formData.append('email', organizationApplyData.email);
        if (organizationApplyData.website) formData.append('website', organizationApplyData.website);
        if (organizationApplyData.country) formData.append('country', organizationApplyData.country);
        if (organizationApplyData.region) formData.append('region', organizationApplyData.region);
        if (organizationApplyData.latitude) formData.append('latitude', organizationApplyData.latitude);
        if (organizationApplyData.longitude) formData.append('longitude', organizationApplyData.longitude);
        if (organizationApplyData.divisionSections) formData.append('divisionSections', organizationApplyData.divisionSections);
        if (organizationApplyData.contactInfo) formData.append('contactInfo', organizationApplyData.contactInfo);
        formData.append('client_id', 'f11f391ea770f9daeef880a66508d733');
        formData.append('client_secret', '122015ebfb35e53469e8d2347524ff59');
        if (organizationApplyData.file instanceof File) {
            formData.append('file', organizationApplyData.file);
        }

        const response = await apiClient.post('/organization/apply', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },
};

// ============================================================================
// 신체 기록 API (웨어러블 기록. 걸음걸이, 심박수 ...)
// ============================================================================
export const physicalRecordAPI = {
    // 신체 기록 조회
    getPhysicalRecords: async (params = {}) => {
        const response = await apiClient.get('/physical-records', { params });
        return response.data;
    },

    // 신체 기록 등록
    registerPhysicalRecord: async recordData => {
        const response = await apiClient.post('/physical-record/register', {
            userId: recordData.userId,
            stepCount: recordData.stepCount,
            heartbeat: recordData.heartbeat,
            info: recordData.info,
        });
        return response.data;
    },
};

// ============================================================================
// 랭킹 API
// ============================================================================
export const rankAPI = {
    // 랭크(누적 경험치) 조회
    getRanks: async (params = {}) => {
        const response = await apiClient.get('/ranks', { params });
        return response.data;
    },

    // 월간 랭크조회
    getMonthlyRanks: async (params = {}) => {
        const response = await apiClient.get('/monthly-ranks', { params });
        return response.data;
    },

    // 챔피언 랭크 조회
    getChampionRanks: async (params = {}) => {
        const response = await apiClient.get('/champion-ranks', { params });
        return response.data;
    },

    // 기관 랭크 조회
    getOrganizationRanks: async (params = {}) => {
        const response = await apiClient.get('/organization-ranks', { params });
        return response.data;
    },
};

// ============================================================================
// 추천 콘텐츠 (AI 기반) API
// ============================================================================
export const recommendationAPI = {
    // 추천 콘텐츠 조회
    getRecommendations: async (params = {}) => {
        const response = await apiClient.get('/recommendations', { params });
        return response.data;
    },

    // 추천 콘텐츠 등록
    registerRecommendation: async recommendationData => {
        const response = await apiClient.post('/recommendation/register', recommendationData);
        return response.data;
    },
};

// ============================================================================
// 데이터 리소스 API
// ============================================================================
export const dataAPI = {
    // 기관 레벨 데이터 가져오기 (캐싱 적용)
    getOrganizationLevels: async () => {
        // localStorage에서 캐시 확인
        const cached = localStorage.getItem('organizationLevels');
        const cacheTime = localStorage.getItem('organizationLevelsCacheTime');

        // 캐시가 있고 1시간 이내라면 캐시 반환
        if (cached && cacheTime) {
            const now = Date.now();
            const cacheAge = now - parseInt(cacheTime);
            const oneHour = 60 * 60 * 1000; // 1시간을 밀리초로

            if (cacheAge < oneHour) {
                return JSON.parse(cached);
            }
        }

        // 캐시가 없거나 만료되었다면 API 호출
        const response = await apiClient.get('/data/organization-levels');

        // 응답을 localStorage에 저장
        if (response.data) {
            localStorage.setItem('organizationLevels', JSON.stringify(response.data));
            localStorage.setItem('organizationLevelsCacheTime', Date.now().toString());
        }

        return response.data;
    },

    // 유저 레벨 데이터 가져오기 (캐싱 적용)
    getUserLevels: async () => {
        // localStorage에서 캐시 확인
        const cached = localStorage.getItem('userLevels');
        const cacheTime = localStorage.getItem('userLevelsCacheTime');

        // 캐시가 있고 1시간 이내라면 캐시 반환
        if (cached && cacheTime) {
            const now = Date.now();
            const cacheAge = now - parseInt(cacheTime);
            const oneHour = 60 * 60 * 1000; // 1시간을 밀리초로

            if (cacheAge < oneHour) {
                return JSON.parse(cached);
            }
        }

        // 캐시가 없거나 만료되었다면 API 호출
        const response = await apiClient.get('/data/user-levels');

        // 응답을 localStorage에 저장
        if (response.data) {
            localStorage.setItem('userLevels', JSON.stringify(response.data));
            localStorage.setItem('userLevelsCacheTime', Date.now().toString());
        }

        return response.data;
    },
};

// ============================================================================
// 코드 API (모바일인증, 메일인증)
// ============================================================================
export const codeAPI = {
    // 코드 조회
    getCodes: async target => {
        const response = await apiClient.get(`/codes/${target}`);
        return response.data;
    },

    // 코드 발행
    generateCode: async codeData => {
        const response = await apiClient.post('/code/generate', {
            target: codeData.target,
            type: codeData.type,
            client_id: 'f11f391ea770f9daeef880a66508d733',
            client_secret: '122015ebfb35e53469e8d2347524ff59',
        });
        return response.data;
    },

    // 발행코드 확인
    confirmCode: async confirmData => {
        const response = await apiClient.post('/code/confirm', {
            target: confirmData.target,
            type: confirmData.type,
            key: confirmData.key,
            client_id: 'f11f391ea770f9daeef880a66508d733',
            client_secret: '122015ebfb35e53469e8d2347524ff59',
        });
        return response.data;
    },
};

// 기본 export
const imsServerAPI = {
    authorizeAPI,
    appAPI,
    challengeAPI,
    championAPI,
    contentAPI,
    dashboardAPI,
    didimAPI,
    eventAPI,
    giftAPI,
    logAPI,
    membershipAPI,
    userAPI,
    noticeAPI,
    faqAPI,
    notificationAPI,
    organizationAPI,
    physicalRecordAPI,
    settingAPI,
    sysconfigAPI,
    dataAPI,
    codeAPI,
};

export default imsServerAPI;
